import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// @ts-ignore
import AsyncSelect from 'react-select/lib/Async';  
import { ModalConfirmationWithErrorAndLoader } from '../common/ModalConfirmationWithErrorAndLoader';
import { checkValidPostcode } from '../../actions/ClientData';
import { executeHttpRequest } from '../../epics/api';
import { TAddress, TPageNotification } from '../../types';
import { getErrorMessages } from '../../utils';

type TAddressFields = {
    address1: string;
    address2: string;
    city: string;
    postcode: string;
}

type TClientAddressesProps = {
    clientId: string;
    notification: TPageNotification;
}

const PAGE_SIZE = 10;

export default function ClientAddresses({
    clientId,
    notification,
}: TClientAddressesProps) {
    const dispatch = useDispatch();

    const [addresses, setAddresses] = useState<TAddress[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [pages, setPages] = useState<number>(0);
    const [showPagination, setShowPagination] = useState<boolean>(false);

    // Add address
    const initialAddFields: TAddressFields = {
        address1: '',
        address2: '',
        city: '',
        postcode: '',
    };
    const [addFields, setAddFields] = useState<TAddressFields>(initialAddFields);
    const [isAdding, setIsAdding] = useState<boolean>(false);

    // Edit address
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingError, setEditingError] = useState<string | null>(null);
    const [editFields, setEditFields] = useState<TAddressFields | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Delete address
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [deletingError, setDeletingError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // Postcode select
    const [selectedPostcode, setSelectedPostcode] = useState(null);

    const fetchAddresses = () => {
        setIsLoading(true);

        executeHttpRequest(`clients/${clientId}/addresses/?paginate_by=${PAGE_SIZE}&page=${page}`)
            .then(response => {
                setIsLoading(false);
                setAddresses(response.data.content);
                if (response.data.meta) {
                    const _pages = Math.ceil(response.data.meta.count / PAGE_SIZE);
                    setPages(_pages);
                    setShowPagination(_pages > 1);
                }
            })
            .catch(err => {
                setIsLoading(false);
                notification.error(
                    'Error',
                    `Could not fetch client addresses: ${getErrorMessages(err)}`,
                );
            });
    };

    useEffect(() => fetchAddresses(), [page]);

    const onEditAddressClick = (address: TAddress) => {
        setEditFields({
            address1: address.address1,
            address2: address.address2,
            city: address.city,
            postcode: address.postcode,
        });
        setEditingId(address.id);
        setEditingError(null);
    };

    const clearAddFields = () => {
        setAddFields(initialAddFields);
        setSelectedPostcode(null);
    };

    const handleAddAddress = () => {
        setIsAdding(true);
        setEditingError(null);

        executeHttpRequest(`clients/${clientId}/addresses/`, 'post', addFields)
            .then(() => {
                setIsAdding(false);
                clearAddFields();
                notification.success('Success', 'Address added successfully');
                // Resetting to page 1 after adding an address
                const targetPage = 1;
                if (targetPage === page) {
                    fetchAddresses();
                } else {
                    setPage(targetPage);
                }
            })
            .catch(err => {
                setIsAdding(false);
                setEditingError(getErrorMessages(err));
            });
    };

    const handleEditAddress = (address: TAddress) => {
        setIsEditing(true);
        setEditingError(null);

        executeHttpRequest(`clients/${clientId}/addresses/${address.id}/`, 'patch', editFields)
            .then(() => {
                setIsEditing(false);
                setEditingId(null);
                notification.success('Success', 'Address updated successfully');
                // Update the list of addresses
                fetchAddresses();
            })
            .catch(err => {
                setIsEditing(false);
                setEditingError(getErrorMessages(err));
            });
    };

    const handleDeleteAddress = () => {
        setIsDeleting(true);
        setDeletingError(null);

        executeHttpRequest(`clients/${clientId}/addresses/${deletingId}/`, 'delete')
            .then(() => {
                setIsDeleting(false);
                setDeletingId(null);
                notification.success('Success', 'Address deleted successfully');
                // Update the list of addresses
                if (page > 1 && addresses.length === 1) {
                    setPage(page - 1);
                } else {
                    fetchAddresses();
                }
            })
            .catch(err => {
                setIsDeleting(false);
                setDeletingError(getErrorMessages(err));
            });
    };

    const renderActionButtons = (address: TAddress) =>
        editingId === address.id
            ? (
                <td className="actions center">
                    <button
                        disabled={isEditing}
                        onClick={() => handleEditAddress(address)}
                        type="button"
                    >
                        <i className="icon icon-check-12" />
                    </button>
                    <button
                        disabled={isEditing}
                        onClick={() => {
                            setEditingId(null);
                            setEditingError(null);
                        }}
                        type="button"
                    >
                        <i className="icon icon-close2" />
                    </button>
                </td>
            ) : (
                <td className="actions center">
                    <button
                        disabled={editingId && editingId !== address.id}
                        onClick={() => onEditAddressClick(address)}
                        type="button"
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                    <button
                        disabled={editingId && editingId !== address.id}
                        onClick={() => {
                            setDeletingId(address.id);
                            setDeletingError(null);
                        }}
                        type="button"
                    >
                        <i className="icon icon-bin-1" />
                    </button>
                </td>
        );

    const renderInputField = (
        fieldName: keyof TAddressFields,
        fields: TAddressFields,
        setFields: (address: TAddressFields) => void,
        placeholder = '',
    ) => (
        <input
            style={{ minWidth: '100%' }}
            name={fieldName}
            type="text"
            maxLength={255}
            value={fields[fieldName]}
            onChange={event => {
                setFields({
                    ...fields,
                    [fieldName]: event.target.value,
                });
            }}
            placeholder={placeholder}
        />
    );

    const renderRows = () => addresses.map((address: TAddress) => (
        <tr key={address.id}>
            {editingId === address.id
                ? (
                    <>
                        <td>{renderInputField('address1', editFields, setEditFields)}</td>
                        <td>{renderInputField('address2', editFields, setEditFields)}</td>
                        <td>{renderInputField('city', editFields, setEditFields)}</td>
                        <td>{renderInputField('postcode', editFields, setEditFields)}</td>
                    </>
                ) : (
                    <>
                        <td>{address.address1}</td>
                        <td>{address.address2}</td>
                        <td>{address.city}</td>
                        <td>{address.postcode}</td>
                    </>
                )
            }
            {renderActionButtons(address)}
        </tr>
    ));

    const renderOption = (option: any) => (
        <div>{[option.address1, option.address2, option.city].filter(Boolean).join(', ')}</div>
    );

    const getOptions = useCallback(
        (inputValue: string, callback: any) => {
            const defaultOptions = [{
                label: inputValue,
                value: -1,
                address1: 'Address not listed',
                address2: '',
                city: '',
                postcode: inputValue,
            }];

            if (inputValue.length < 5) {
                return callback(defaultOptions);
            }

            return dispatch(checkValidPostcode(inputValue, (response: any, error: any) => {
                if (error) {
                    return callback(null, { options: defaultOptions, complete: true });
                }

                const fetchedOptions = response.content.map(
                    (address: any, idx: number) => ({
                        label: address.postcode,
                        value: idx,
                        address1: address.line_1,
                        address2: address.line_2,
                        city: address.post_town,
                        postcode: address.postcode,
                    })
                );
                return callback(null, { options: fetchedOptions.concat(defaultOptions), complete: true });
            }));
        }, [dispatch]
    );


    const renderAddAddressRow = () => (
        <tr>
            <td>{renderInputField('address1', addFields, setAddFields, 'Address 1')}</td>
            <td>{renderInputField('address2', addFields, setAddFields, 'Address 2')}</td>
            <td>{renderInputField('city', addFields, setAddFields, 'City')}</td>
            <td>
                <AsyncSelect
                    onChange={(option: any) => {
                        setSelectedPostcode(option);
                        setAddFields({
                            address1: option.value !== -1 ? option.address1 : '',
                            address2: option.address2,
                            city: option.city,
                            postcode: option.postcode,
                        });
                    }}
                    placeholder="Postcode"
                    value={selectedPostcode}
                    optionRenderer={renderOption}
                    loadOptions={getOptions}
                    filterOption={() => true}
                    clearable={false}
                    cache={false}
                />
            </td>
            <td className="actions center">
                <button
                    disabled={isAdding || !addFields.address1 || !addFields.postcode}
                    onClick={handleAddAddress}
                    type="button"
                >
                    <i className="icon icon-add-12" />
                </button>
                <button
                    disabled={isAdding || !Object.values(addFields).some(field => field !== '')}
                    onClick={clearAddFields}
                    type="button"
                >
                    <i className="icon icon-close2" />
                </button>
            </td>
        </tr>
    ); 


    const renderAddressDetails = (id: number) => {
        const address = addresses.find((item: TAddress) => item.id === id);
        if (address) {
            return (
                <div style={{ marginTop: '20px', textAlign: 'left' }}>
                    <p><strong>Address 1:</strong> {address.address1}</p>
                    <p><strong>Address 2:</strong> {address.address2}</p>
                    <p><strong>City:</strong> {address.city}</p>
                    <p><strong>Postcode:</strong> {address.postcode}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">Locations</div>
            </div>
            <div className="freelancer-details-section__content">
                {!!editingError && (
                    <div className="freelancer-details-section__errors">
                        <p data-testid="errorMsg">{editingError}</p>
                    </div>
                )}
                <table className="table-default table-layout-fixed table-default__no-fixed-first-child-width">
                    <tbody>
                        <tr>
                            <td className="table-default__cell--text-blue">Address 1</td>
                            <td className="table-default__cell--text-blue">Address 2</td>
                            <td className="table-default__cell--text-blue med-col">City</td>
                            <td className="table-default__cell--text-blue med-col">Postcode</td>
                            <td className="table-default__cell--text-blue center table-default__cell--action">
                                Action
                            </td>
                        </tr>
                        {renderRows()}
                        {renderAddAddressRow()}
                    </tbody>
                </table>
                {showPagination && (
                    <div className="table-default-pagination">
                        <button
                            className="pagination-text"
                            type="button"
                            onClick={() => setPage(page - 1)}
                            disabled={isLoading || page === 1}
                        >
                            Previous
                        </button>
                        <span className="pagination-text">Page {page} of {pages}</span>
                        <button
                            className="pagination-text"
                            type="button"
                            onClick={() => setPage(page + 1)}
                            disabled={isLoading || page === pages}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
            {!!deletingId && (
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={deletingError}
                    handleCancel={() => {
                        setDeletingId(null);
                        setDeletingError(null);
                    }}
                    handleContinue={handleDeleteAddress}
                    isError={!!deletingError}
                    isInProgress={isDeleting}
                    setIsInProgress={setIsDeleting}
                    isOpen={!!deletingId}
                    title="Delete Location"
                >
                    <p className="modal-title">Delete Location</p>
                    <p>Are you sure you want to delete this location?</p>
                    {renderAddressDetails(deletingId)}
                </ModalConfirmationWithErrorAndLoader>
            )}
        </div>
    );
}
